import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import {
  listRejectionReasons,
  createRejectionReason,
  updateRejectionReason,
  deleteRejectionReason,
} from '../../api/masters';
import { pushToast } from '../../components/ui/toastBus';

function ReasonFormModal({ open, onClose, reason }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(reason?.name || '');
    setIsActive(reason?.is_active ?? true);
    setError('');
  }, [reason]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), is_active: isActive };
      return reason ? updateRejectionReason(reason.id, payload) : createRejectionReason(payload);
    },
    onSuccess: () => {
      pushToast({ type: 'success', message: reason ? 'Reason updated.' : 'Reason created.' });
      queryClient.invalidateQueries({ queryKey: ['rejection-reasons'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not save reason.'),
  });

  const handleSave = () => {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setError('');
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={reason ? 'Edit rejection reason' : 'New rejection reason'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Reason" value={name} onChange={(e) => setName(e.target.value)} />
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active (offered when rejecting a step)
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

export default function RejectionReasonMasters() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reasonsQuery = useQuery({
    queryKey: ['rejection-reasons', { include_inactive: true }],
    queryFn: () => listRejectionReasons({ include_inactive: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRejectionReason,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Reason deleted.' });
      queryClient.invalidateQueries({ queryKey: ['rejection-reasons'] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete reason.' });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: 'name', label: 'Reason' },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => <Badge tone={row.is_active ? 'green' : 'slate'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="masters.manage">
            <button
              onClick={() => {
                setFormTarget(row);
                setFormOpen(true);
              }}
              className="text-primary hover:underline"
            >
              Edit
            </button>
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Rejection Reasons"
        description="Predefined reasons staff pick from when rejecting or failing a step."
        actions={
          <Can perm="masters.manage">
            <Button
              onClick={() => {
                setFormTarget(null);
                setFormOpen(true);
              }}
            >
              New Reason
            </Button>
          </Can>
        }
      />

      <Table columns={columns} data={reasonsQuery.data || []} isLoading={reasonsQuery.isLoading} emptyMessage="No rejection reasons yet." />

      {formOpen && <ReasonFormModal open={formOpen} onClose={() => setFormOpen(false)} reason={formTarget} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete rejection reason"
        message={`Delete "${deleteTarget?.name}"? Past rejections keep the text they were saved with.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
